import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import Seo, { ORGANIZATION_SCHEMA, buildBreadcrumbSchema, buildFaqSchema, SITE_URL } from "@/components/Seo";
import { ArticleCard } from "@/components/ArticleCard";
import { GuideCta } from "@/components/GuideCta";
import { PillarEditorial, PillarSources } from "@/components/PillarEditorial";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";
import { useSiteContent } from "@/contexts/SiteContentContext";
import ROUTES_CONTENT from "@/lib/howWeHelpRoutes.json";
import PILLARS from "@/lib/pillars.json";
import { getPillar, pillarPath } from "@/lib/seoArchitecture";
import NotFound from "@/pages/NotFound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const origin = () => SITE_URL;

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="text-[11px] tracking-[0.14em] uppercase text-[#897756]" style={K}>
      <ol className="flex flex-wrap gap-x-2 gap-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden="true">/</span>}
            {it.url && i < items.length - 1 ? <Link data-testid={`breadcrumb-link-${i}`} to={it.url} className="hover:text-[#451C25] underline-offset-4 hover:underline">{it.name}</Link> : <span data-testid="breadcrumb-current" aria-current="page" className="text-[#451C25]">{it.name}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Country + route pillar page (indexable). Content comes from pillars.json keyed by country → route slug.
export default function RoutePage() {
  const { country, route } = useParams();
  const { pathname } = useLocation();
  const p = getPillar(country, route);
  const [posts, setPosts] = useState([]);
  const { section } = useSiteContent();
  const hwh = section("how_we_help_routes", ROUTES_CONTENT);

  useEffect(() => {
    axios.get(`${API}/posts`).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  if (!p) return <NotFound />;
  const path = `/${country}/${route}/`;
  if (pathname !== path) return <Navigate to={path} replace />;
  const featured = p.featured.map((s) => posts.find((x) => x.slug === s)).filter(Boolean);
  const crumbs = [{ name: "Home", url: "/" }, ...(route !== "moving-to-spain" ? [{ name: "Moving to Spain", url: pillarPath(country, "general") }] : []), { name: p.breadcrumb, url: path }];
  const faq = (p.faq || []).map(([q, a]) => ({ q, a }));
  const analytics = { country, route: p.route, page_path: path };

  return (
    <main data-testid={`pillar-${country}-${route}`} className="bg-[#F5EFE7] text-[#451C25] pt-[136px] lg:pt-[126px]">
      <Seo title={p.seo_title} description={p.meta_description} path={path} image={p.image} jsonLd={[ORGANIZATION_SCHEMA, buildBreadcrumbSchema(crumbs.map((c) => ({ name: c.name, url: `${origin()}${c.url}` }))), ...(faq.length && p.faq_schema !== false ? [buildFaqSchema(faq)] : [])]} />
      <Header />

      <section className="px-6 md:px-10 lg:px-14 pt-10 md:pt-14 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Breadcrumbs items={crumbs} />
            <h1 data-testid="pillar-h1" className="mt-8 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ ...K, fontWeight: 600 }}>{p.h1}</h1>
            <p className="mt-7 text-[#6B4954] text-base md:text-lg leading-relaxed max-w-2xl" style={S}>{p.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              {p.primary_cta === "call" ? <ScheduleCallButton tone="burgundy" label="Schedule a Free Call" source={`pillar_${country}_${route}_hero`} extra={analytics} /> : <GuideCta placement={`pillar_${country}_${route}_hero`} variant="compact" prompt="" extra={analytics} />}
              {p.primary_cta === "call" && !p.secondary_cta && <GuideCta placement={`pillar_${country}_${route}_hero`} variant="compact" extra={analytics} />}
              {p.secondary_cta && <Link to={p.secondary_cta[1]} data-testid="pillar-secondary-cta" className="group inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-semibold border-b border-[#B3A17D] pb-1" style={K}>{p.secondary_cta[0]} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></Link>}
            </div>
          </div>
          <div className="lg:col-span-5"><img src={p.image} alt={p.image_alt || ""} className="w-full aspect-[4/3] object-cover" /></div>
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20 border-t border-[#451C25]/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>{p.who_title}</h2>
          {p.paths ? (
            <ul data-testid="pillar-paths" className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {[["work", "Work from Spain", "Keep your job, change where your life happens."], ["retire", "Retire in Spain", "An active next chapter built around how you want to live."], ["home", "Buy a Home", "The right home, in the right place, with the process understood."]].map(([r, t, d]) => (
                <li key={r}>
                  <a href={pillarPath(country, r)} data-testid={`pillar-path-${r}`} className="group block border-t-2 border-[#B3A17D] pt-5 hover:opacity-80 transition-opacity">
                    <span className="block text-2xl" style={{ ...K, fontWeight: 500 }}>{t}</span>
                    <span className="block mt-2 text-[#6B4954] text-[15px]" style={S}>{d}</span>
                    <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold" style={K}>Explore this path <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
              {p.who.map(([t, d], i) => <li key={i} className="border-l-2 border-[#B3A17D] pl-5"><p className="text-lg" style={{ ...K, fontWeight: 500 }}>{t}</p><p className="mt-1 text-[#6B4954] text-[15px]" style={S}>{d}</p></li>)}
            </ul>
          )}
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20 bg-white/40">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>{p.work_through_title}</h2>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-8">
            {p.work_through.map(([t, d], i) => <li key={i}><p className="text-[#897756] text-[11px] tracking-[0.26em] uppercase font-semibold" style={K}>0{i + 1}</p><p className="mt-2 text-xl" style={{ ...K, fontWeight: 500 }}>{t}</p><p className="mt-2 text-[#6B4954] text-[15px] leading-relaxed" style={S}>{d}</p></li>)}
          </ul>
        </div>
      </section>

      <PillarEditorial sections={p.sections} route={route} />

      <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20 bg-[#183129] text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>{p.path_title}</h2>
          <ol className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-8">
            {p.path.map(([t, d], i) => <li key={i} className="border-t border-white/20 pt-5"><p className="text-[#B3A17D] text-2xl" style={K}>0{i + 1}</p><p className="mt-2 text-xl" style={{ ...K, fontWeight: 500 }}>{t}</p><p className="mt-2 text-white/75 text-[15px]" style={S}>{d}</p></li>)}
          </ol>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>Relevant resources</h2>
              <Link to="/resources" data-testid="pillar-all-resources" className="text-[12px] tracking-[0.18em] uppercase font-semibold border-b border-[#B3A17D] pb-1" style={K}>All resources</Link>
            </div>
            <ul data-testid="pillar-resources" className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12">
              {featured.map((post) => <li key={post.slug}><ArticleCard post={post} /></li>)}
              {(p.extra_links || []).map(([t, u], i) => <li key={u} className="border-t-2 border-[#B3A17D] pt-5"><Link to={u} data-testid={`pillar-extra-link-${i}`} className="text-xl hover:text-[#6B4954]" style={{ ...K, fontWeight: 500 }}>{t}</Link><p className="mt-2 text-[#6B4954] text-[15px]" style={S}>Practical guide to Spain's tax identification number.</p></li>)}
            </ul>
          </div>
        </section>
      )}

      <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20 border-t border-[#451C25]/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5"><h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>How Destino Europe helps</h2></div>
          <p className="lg:col-span-7 text-[#6B4954] text-base md:text-lg leading-relaxed" style={S}>{hwh.scope_note}</p>
        </div>
      </section>

      {faq.length > 0 && (
        <section className="px-6 md:px-10 lg:px-14 py-16 md:py-20 bg-white/40">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>Common questions</h2>
            <dl data-testid="pillar-faq" className="mt-8 divide-y divide-[#451C25]/15">
              {faq.map((f, i) => <div key={i} className="py-5"><dt className="text-lg" style={{ ...K, fontWeight: 500 }}>{f.q}</dt><dd className="mt-2 text-[#6B4954] text-[15px] leading-relaxed" style={S}>{f.a}</dd></div>)}
            </dl>
          </div>
        </section>
      )}

      <PillarSources sources={p.sources} reviewed={p.last_reviewed} route={route} />

      <section data-testid="pillar-final-cta" className="px-6 md:px-10 lg:px-14 py-20 md:py-24 bg-[#451C25] text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-3xl md:text-4xl leading-tight" style={{ ...K, fontWeight: 500 }}>{p.cta_heading}</p>
            <div className="mt-6">{p.primary_cta === "call" ? <ScheduleCallButton tone="sand" label="Schedule a Free Call" source={`pillar_${country}_${route}_final`} extra={analytics} /> : <GuideCta placement={`pillar_${country}_${route}_final`} tone="dark" heading={p.guide_heading} extra={analytics} />}</div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">{p.primary_cta === "call" ? <GuideCta placement={`pillar_${country}_${route}_final`} tone="dark" extra={analytics} /> : <ScheduleCallButton tone="sand" size="sm" label="Schedule a Free Call" source={`pillar_${country}_${route}_final`} extra={analytics} />}</div>
        </div>
      </section>
      <Footer />
    </main>
  );
}

export { PILLARS };
